import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { Band } from '../../results-vm';
import { DomainBandConfig, ScaleLevel, bandScale } from '../../results-placeholders';

/**
 * wordfirst sec 2 — the word-first domain header: the evaluation WORD as an 18px
 * title with a colour dot, above a 3-level bar (three 8px segments, 4px gap, NO
 * text inside), with the level names as axis captions below. The levels run
 * best -> worst in DOM order (reading order), so the weakest level always sits
 * at the far end of the bar, in every domain, whatever the number behind it. Deliberately NOT a segmented control: no per-zone border,
 * radius, padding or pointer — at 8px with no in-zone label it reads as a meter,
 * never as something to tap. Green (best) / amber (middle) / deep amber (worst) —
 * the deep amber is the same hue as amber, only darker, so the worst tier reads
 * distinct from the middle without a warning colour (wordfirst 03.09).
 */
@Component({
  selector: 'app-band-scale',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="band" *ngIf="band">
      <div class="band-verdict">
        <span class="ov-dot" [style.background]="dotColor"></span>{{ band.labelKey | translate }}
      </div>
      <div class="band-track" aria-hidden="true">
        <div
          class="band-zone"
          *ngFor="let z of levels"
          [class.on]="z.on"
          [class.amber]="z.on && z.scaleColor === 'amber'"
          [class.green]="z.on && z.scaleColor === 'green'"
          [class.deep]="z.on && z.scaleColor === 'deep'"
        ></div>
      </div>
      <div class="band-caps" aria-hidden="true">
        <span
          *ngFor="let z of levels"
          [class.on]="z.on"
          [class.amber]="z.on && z.scaleColor === 'amber'"
          [class.green]="z.on && z.scaleColor === 'green'"
          [class.deep]="z.on && z.scaleColor === 'deep'"
        >{{ z.labelKey | translate }}</span>
      </div>
      <div class="band-about" *ngIf="aboutKey">
        <button
          type="button"
          class="band-about-btn"
          (click)="aboutOpen = !aboutOpen"
          [attr.aria-expanded]="aboutOpen"
        >
          {{ 'results.about.label' | translate }}
        </button>
        <p class="band-about-text" *ngIf="aboutOpen">{{ aboutKey | translate }}</p>
      </div>
    </div>
  `,
  styles: [
    `
      .band-about {
        margin-top: 6px;
      }
      /* B3: keeps the 44px touch-target rule; underlined text so it reads as a
         disclosure, never as part of the meter above it. */
      .band-about-btn {
        background: none;
        border: 0;
        padding: 4px 0;
        min-height: 44px;
        font: inherit;
        font-size: 12px;
        color: var(--g600, #6b7280);
        text-decoration: underline;
        cursor: pointer;
      }
      .band-about-text {
        margin: 2px 0 0;
        font-size: 12px;
        line-height: 1.6;
        color: var(--g600, #6b7280);
      }
    `,
  ],
})
export class BandScaleComponent implements OnChanges {
  @Input() band: Band | null = null;
  @Input() config!: DomainBandConfig;
  @Input() score: number | null = null;
  /** B9: i18n key of a short "About this measure" explanation. Omit to hide. */
  @Input() aboutKey: string | null = null;

  aboutOpen = false;

  levels: ScaleLevel[] = [];

  ngOnChanges(): void {
    this.levels =
      this.config && this.score != null ? bandScale(this.config, this.score) : [];
  }

  get dotColor(): string {
    switch (this.band?.tone) {
      case 'green':
      case 'teal':
        return 'var(--status-green)';
      case 'amberDeep':
        return 'var(--status-amber-deep)';
      default:
        return 'var(--status-amber)';
    }
  }
}
